import React from "react";
import {
  AdvancedMarker,
  Map,
  Pin,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import Autocomplete, {
  type AutocompleteInputChangeReason,
} from "@mui/material/Autocomplete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { useField } from "formik";
import type { IAddress } from "@/services/model/address";
import {
  createAutocompleteSessionToken,
  fetchPlaceAutocompleteOptions,
  resolvePlaceSelection,
  type GooglePlaceOption,
} from "./googlePlaces";

type Option = GooglePlaceOption;

type AddressResponse = {
  id: string;
  googleAddress: string;
  latitude: number;
  longitude: number;
};

type AddressListResponse = {
  data?: AddressResponse[];
};

const DEFAULT_CENTER = { lat: -26.2041, lng: 28.0473 };

const isAddressListResponse = (value: unknown): value is AddressListResponse => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as AddressListResponse;
  return candidate.data === undefined || Array.isArray(candidate.data);
};

type Props = {
  id?: string;
  name: string;
  addressFields: IAddress;
  required: boolean | undefined;
  setAddressFields: React.Dispatch<React.SetStateAction<IAddress>>;
  enableMap?: boolean;
  token: string;
  GetAddressList: (token: string) => Promise<unknown>;
  showDialog?: (title: string, message: string, success?: boolean) => void;
};

export default function GoogleAddressInner({
  id,
  name,
  addressFields,
  required,
  setAddressFields,
  enableMap = true,
  token,
  GetAddressList,
  showDialog,
}: Props) {
  const placesLibrary = useMapsLibrary("places");
  const map = useMap();
  const [, meta, helpers] = useField<string>(name);
  const hasError = Boolean(meta.touched && meta.error);

  const [options, setOptions] = React.useState<Option[]>([]);
  const [inputValue, setInputValue] = React.useState(
    addressFields.googleAddress || "",
  );
  const [isSearching, setIsSearching] = React.useState(false);
  const sessionTokenRef =
    React.useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const [addresses, setAddresses] = React.useState<AddressResponse[]>([]);
  const [loadingExisting, setLoadingExisting] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const reqRef = React.useRef(0);
  const debRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const next = addressFields.googleAddress || "";
    if (next !== inputValue) {
      setInputValue(next);
    }
  }, [addressFields.googleAddress, inputValue]);

  React.useEffect(() => {
    if (placesLibrary && !sessionTokenRef.current) {
      sessionTokenRef.current = createAutocompleteSessionToken();
    }
  }, [placesLibrary]);

  React.useEffect(() => {
    return () => {
      if (debRef.current) {
        window.clearTimeout(debRef.current);
      }
    };
  }, []);

  const handleInputChange = React.useCallback(
    (
      _event: React.SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      setInputValue(value);

      if (reason === "input" || reason === "clear") {
        setAddressFields((prev) => ({
          ...prev,
          googleAddress: value,
          ...(reason === "clear"
            ? { latitude: undefined, longitude: undefined }
            : null),
        }));
      }

      if (!placesLibrary || !sessionTokenRef.current) {
        setOptions([]);
        return;
      }

      if (reason === "clear") {
        sessionTokenRef.current = createAutocompleteSessionToken();
      }

      if (reason !== "input") {
        setOptions([]);
        return;
      }

      if (debRef.current) {
        window.clearTimeout(debRef.current);
      }

      debRef.current = window.setTimeout(() => {
        const rid = ++reqRef.current;

        if (!value) {
          setIsSearching(false);
          setOptions([]);
          return;
        }

        setIsSearching(true);

        void (async () => {
          try {
            const nextOptions = await fetchPlaceAutocompleteOptions(
              value,
              sessionTokenRef.current!,
            );

            if (rid !== reqRef.current) {
              return;
            }

            setOptions(nextOptions);
          } catch (error) {
            if (rid !== reqRef.current) {
              return;
            }

            console.error("Failed to fetch Google place suggestions.", error);
            setOptions([]);
          } finally {
            if (rid === reqRef.current) {
              setIsSearching(false);
            }
          }
        })();
      }, 150);
    },
    [placesLibrary, setAddressFields],
  );

  const handlePlaceSelected = (
    _event: React.SyntheticEvent<Element, Event>,
    value: string | Option | null,
  ) => {
    if (!value || typeof value === "string") {
      setAddressFields((prev) => ({
        ...prev,
        googleAddress: typeof value === "string" ? value : "",
        latitude: undefined,
        longitude: undefined,
      }));
      setInputValue(typeof value === "string" ? value : "");
      sessionTokenRef.current = placesLibrary
        ? createAutocompleteSessionToken()
        : null;
      return;
    }

    if (!placesLibrary) {
      return;
    }

    void (async () => {
      try {
        const place = await resolvePlaceSelection(value);
        if (!place) {
          return;
        }

        setAddressFields((prev) => ({
          ...prev,
          googleAddress: place.description,
          latitude: place.latitude,
          longitude: place.longitude,
        }));

        setInputValue(place.description);

        if (map && enableMap) {
          map.panTo({ lat: place.latitude, lng: place.longitude });
          map.setZoom(15);
        }
      } catch (error) {
        console.error("Failed to fetch Google place details.", error);
      } finally {
        sessionTokenRef.current = createAutocompleteSessionToken();
      }
    })();
  };

  const fetchAddress = React.useCallback(async () => {
    setLoadingExisting(true);
    try {
      const responseList = await GetAddressList(token);
      if (
        isAddressListResponse(responseList) &&
        Array.isArray(responseList.data)
      ) {
        setAddresses(
          responseList.data.map((r: AddressResponse) => ({
            id: r.id,
            googleAddress: r.googleAddress,
            latitude: r.latitude,
            longitude: r.longitude,
          })),
        );
      }
    } catch (err) {
      const msg = "An exception occurred, please contact your administrator.";
      if (showDialog) {
        showDialog("Failure", msg);
      } else {
        console.error(msg);
      }
      if (err instanceof Error) {
        console.error(err.message);
      }
    } finally {
      setLoadingExisting(false);
    }
  }, [GetAddressList, token, showDialog]);

  const handleOpenExistingClick = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    if (addresses.length === 0) {
      await fetchAddress();
    }
  };

  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleExistingAddressSelected = (addr: AddressResponse) => {
    setAddressFields((prev) => ({
      ...prev,
      googleAddress: addr.googleAddress,
      latitude: addr.latitude,
      longitude: addr.longitude,
    }));

    setInputValue(addr.googleAddress || "");

    if (map && enableMap) {
      map.panTo({ lat: addr.latitude, lng: addr.longitude });
      map.setZoom(15);
    }

    handleCloseMenu();
  };

  const hasPoint =
    addressFields.latitude != null && addressFields.longitude != null;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
    >
      <Autocomplete
        fullWidth
        freeSolo
        options={options}
        loading={isSearching}
        isOptionEqualToValue={(option, value) =>
          typeof value !== "string" &&
          option.placePrediction.placeId === value.placePrediction.placeId
        }
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description || ""
        }
        inputValue={inputValue}
        noOptionsText={
          inputValue.trim().length > 0
            ? "No South African address or business matches found"
            : "Type a South African address or business name"
        }
        onInputChange={handleInputChange}
        onChange={handlePlaceSelected}
        renderInput={(params) => (
          <TextField
            {...params}
            id={id || name}
            name={name}
            label="Search address or business"
            variant="outlined"
            error={hasError}
            helperText={
              hasError
                ? meta.error
                : "Search for a South African address, premises, or business."
            }
            required={!!required}
            onBlur={() => helpers.setTouched(true)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  {isSearching ? (
                    <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                  ) : null}
                  <Tooltip title="Use an existing address">
                    <IconButton
                      size="small"
                      onClick={handleOpenExistingClick}
                      tabIndex={-1}
                    >
                      {loadingExisting ? (
                        <CircularProgress size={18} />
                      ) : (
                        <ArrowDropDownIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </>
              ),
            }}
          />
        )}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {loadingExisting && (
          <MenuItem disabled>
            <CircularProgress size={18} style={{ marginRight: 8 }} />
            Loading addresses...
          </MenuItem>
        )}
        {!loadingExisting && addresses.length === 0 && (
          <MenuItem disabled>No saved addresses found</MenuItem>
        )}
        {!loadingExisting &&
          addresses.map((addr) => (
            <MenuItem
              key={addr.id}
              onClick={() => handleExistingAddressSelected(addr)}
            >
              {addr.googleAddress}
            </MenuItem>
          ))}
      </Menu>

      {enableMap && (
        <Map
          center={
            hasPoint
              ? {
                  lat: Number(addressFields.latitude),
                  lng: Number(addressFields.longitude),
                }
              : DEFAULT_CENTER
          }
          zoom={hasPoint ? 15 : 11}
          mapId="DEMO_MAP_ID"
          gestureHandling="greedy"
          style={{ width: "100%", height: 280 }}
        >
          {hasPoint && (
            <AdvancedMarker
              position={{
                lat: Number(addressFields.latitude),
                lng: Number(addressFields.longitude),
              }}
            >
              <Pin background="red" borderColor="white" glyphColor="white" />
            </AdvancedMarker>
          )}
        </Map>
      )}
    </Box>
  );
}
